import React, { useEffect, useState } from 'react'
import { Box, Divider } from '@mui/material'

import { useAuth } from '../AuthContext/AuthProvider'
import { useApp } from '../AppContext/AppProvider'

import PhoneVerification from './PhoneVerification.component'
import {
  InitiatePhoneButtonEvent,
  NewCodeButtonEvent,
  PhoneContainerPhoneState,
  PhoneVerificationData,
  PHONE_VERIFICATION,
  ScreenOpenedEvent,
  ScreenSmsCodeEvent,
  SubmitCodeButtonEvent
} from './types'
import { ProgressStepper } from '../ProgressStepper/Progress.component'
import { TOTAL_STEPS } from '../ApplicationProgress/types'
import { ConnectedDotsIcon } from '../Icons/ConnectedDotsIcon'
import { PhoneVerificationFormComponent } from './PhoneVerification.form.component'
import {
  sendTrackingEvent,
  toButtonEvent,
  toScreenEvent
} from '../../services/amplitude.service'

const PhoneVerificationContainer: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isInvalid, setIsInvalid] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [phoneEntered, setPhoneEntered] = useState<string>('')

  const { getCredentials } = useAuth()
  const { application } = useApp()

  const handlePhoneInitiation = ({
    primaryPhone
  }: PhoneContainerPhoneState): Promise<void> => {
    setIsError(false)
    setIsInvalid(false)
    setIsLoading(true)
    sendTrackingEvent(toButtonEvent({ ...InitiatePhoneButtonEvent }))

    return application
      .service('auth/credentials')
      .patch(null, { phoneNumber: `+${primaryPhone}` })
      .then(() => {
        sendTrackingEvent(toScreenEvent({ ...ScreenSmsCodeEvent }))
        setPhoneEntered(primaryPhone)
      })
      .then(() => handlePhoneNewCode())
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false))
  }

  const handleButtonGetNewCode = (): Promise<void> => {
    sendTrackingEvent(toButtonEvent({ ...NewCodeButtonEvent }))
    return handlePhoneNewCode()
  }

  const handlePhoneNewCode = (): Promise<void> => {
    setIsError(false)
    setIsInvalid(false)
    setIsLoading(true)

    return application
      .service('auth/credentials/confirmation')
      .create({ type: PHONE_VERIFICATION })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false))
  }

  const handleOnSubmitCode = (data: PhoneVerificationData): Promise<void> => {
    setIsError(false)
    setIsInvalid(false)
    setIsLoading(true)
    sendTrackingEvent(toButtonEvent({ ...SubmitCodeButtonEvent }))

    return application
      .service('auth/credentials/confirmation')
      .update(null, data)
      .then(getCredentials)
      .catch((e: any) => {
        // TODO: Figure out the types of error and improve handling
        if (
          e.error !== 'Invalid verification code provided, please try again.'
        ) {
          setIsError(true)
        }
        setIsInvalid(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    if (!phoneEntered) {
      sendTrackingEvent(toScreenEvent({ ...ScreenOpenedEvent }))
    }
  }, [])

  return (
    <Box sx={{ p: 5 }}>
      <Box sx={{ mt: -4, mb: 2, pr: 4, pl: 4 }}>
        <ProgressStepper step={2} totalSteps={TOTAL_STEPS} />
      </Box>

      <Divider sx={{ mt: -2.5, mr: -5, ml: -5 }} variant="fullWidth" />

      <div style={{ textAlign: 'center', marginBottom: 20, marginTop: 20 }}>
        <ConnectedDotsIcon sx={{ height: '1em', width: '2em' }} />
      </div>

      {!phoneEntered ? (
        <PhoneVerificationFormComponent
          onSumbit={handlePhoneInitiation}
          isError={isError}
          isLoading={isLoading}
        />
      ) : (
        <PhoneVerification
          phone={phoneEntered}
          phoneNewCode={handleButtonGetNewCode}
          onSubmitCode={handleOnSubmitCode}
          isInvalid={isInvalid}
          isError={isError}
          isLoading={isLoading}
        />
      )}
    </Box>
  )
}

export default PhoneVerificationContainer
